// Frontend types for Waves Partnerportaal (no sensitive fields like password_hash)

export interface Partner {
  id: number;
  name: string;
  logo_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
  created_at: string;
  review_mail_day: number;
  review_mail_hour: number;
  review_mail_enabled: boolean;
}

export interface Tab {
  id: number;
  name: string;
  partner_id: number;
  location: string;
  created_at: string;
}

export interface UserPublic {
  id: number;
  name: string;
  email: string;
  role: "admin" | "kam" | "hmp" | "partner_admin";
  partner_id: number | null;
  kam_id: number | null;
  tab_permissions: number[];
  review_interval_days: number | null;
  last_login?: string | null;
  inactivity_alert_days?: number | null;
  no_changes_alert_days?: number | null;
  last_activity_at?: string | null;
}

export interface ScheduledRun {
  id: number;
  partner_id: number;
  partner_name: string;
  scheduled_for: string;
  status: "pending" | "kam_warned" | "completed" | "cancelled";
  hmp_count: number;
  hmps: { id: number; name: string; email: string; kam_id: number | null; kam_name: string | null; excluded: boolean }[];
}

export interface KamVacation {
  id: number;
  kam_id: number;
  kam_name: string;
  substitute_kam_id: number;
  substitute_kam_name: string;
  start_date: string;
  end_date: string;
  status: "scheduled" | "active" | "finished" | "cancelled";
  created_at: string;
}

export type ExperienceLevel = "nvt" | "starter" | "1_3" | "3_5" | "5_10" | "10_plus";
export type Discipline =
  | "nvt"
  | "combi"
  | "water"
  | "elektra_laag"
  | "elektra_hoog"
  | "gas"
  | "riool"
  | "overig";

export const EXPERIENCE_LABELS: Record<ExperienceLevel, string> = {
  nvt: "N.v.t.",
  starter: "Starter",
  "1_3": "1–3 jaar",
  "3_5": "3–5 jaar",
  "5_10": "5–10 jaar",
  "10_plus": "10+ jaar",
};

export const DISCIPLINE_LABELS: Record<Discipline, string> = {
  nvt: "N.v.t.",
  combi: "Combi",
  water: "Water",
  elektra_laag: "Elektra laagspanning",
  elektra_hoog: "Elektra hoogspanning",
  gas: "Gas",
  riool: "Riool",
  overig: "Overig",
};

export interface Vacature {
  id: number;
  functie: string;
  partner_id: number;
  tab_id: number;
  status: "open" | "gesloten" | "on_hold";
  priority: "hoog" | "normaal" | "laag";
  contactpersoon: string | null;
  locatie: string | null;
  aantal_posities: number;
  bemiddeling_status: "geen" | "in_bemiddeling" | "shortlisted" | "geplaatst";
  opmerkingen: string | null;
  experience_level: ExperienceLevel;
  discipline: Discipline;
  discipline_overig_text: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
}

export interface AuditEntry {
  id: number;
  user_id: number;
  user_name: string;
  action: "create" | "update" | "delete" | "status_change" | "login";
  vacature_id: number | null;
  vacature_functie: string | null;
  partner_name: string | null;
  details: string | null;
  created_at: string;
}

export interface Notification {
  id: number;
  user_id: number;
  message: string;
  type: "info" | "warning" | "success";
  related_vacature_id: number | null;
  is_read: number;
  created_at: string;
}

export interface AdminDashboardData {
  totaal_vacatures: number;
  open_vacatures: number;
  gesloten_vacatures: number;
  hoge_prioriteit: number;
  in_bemiddeling: number;
  geplaatst: number;
  per_partner: {
    partner_name: string;
    open: number;
    gesloten: number;
    hoog: number;
    in_bemiddeling: number;
  }[];
  recent_activity: AuditEntry[];
}

export interface HMPDashboardData {
  open_vacatures: number;
  gesloten_vacatures: number;
  hoge_prioriteit: number;
  in_bemiddeling: number;
  stale_vacatures: {
    id: number;
    functie: string;
    locatie: string;
    days_since_update: number;
    opmerkingen: string | null;
    status: string;
    priority: string;
    bemiddeling_status: string;
    contactpersoon: string | null;
    aantal_posities: number;
  }[];
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: UserPublic;
  token: string;
}

export interface CreateVacatureRequest extends VacatureExtraFields {
  functie: string;
  partner_id: number;
  tab_id: number;
  priority?: string;
  contactpersoon?: string;
  locatie?: string;
  aantal_posities?: number;
  opmerkingen?: string;
}

interface VacatureExtraFields {
  experience_level?: ExperienceLevel;
  discipline?: Discipline;
  discipline_overig_text?: string | null;
}

export interface UpdateVacatureRequest extends VacatureExtraFields {
  functie?: string;
  status?: string;
  priority?: string;
  contactpersoon?: string;
  locatie?: string;
  aantal_posities?: number;
  bemiddeling_status?: string;
  opmerkingen?: string;
  tab_id?: number;
}
